import { useEffect, useRef, useState } from 'react'
import { Download, Info, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { useImportFromAzureDevOps, useAzureDevOpsProcessConfig } from '@/hooks'
import { WorkItemStatuses, type ImportAzureDevOpsWorkItemFailure } from '@/types'
import { parseCsvFile, buildTypeSets, type PreviewRow } from './importAzureDevOpsCsv'

interface ImportAzureDevOpsDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImportAzureDevOpsDialog({ projectId, open, onOpenChange }: ImportAzureDevOpsDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [failedRows, setFailedRows] = useState<ImportAzureDevOpsWorkItemFailure[]>([])
  const [isTreeFormat, setIsTreeFormat] = useState(false)
  const importMutation = useImportFromAzureDevOps(projectId)
  const processConfig = useAzureDevOpsProcessConfig(projectId, open)

  useEffect(() => {
    if (!open) {
      setFileName(null)
      setPreviewRows([])
      setError(null)
      setIsDragging(false)
      setFailedRows([])
      setIsTreeFormat(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [open])

  const parseFile = async (file: File) => {
    setError(null)
    setPreviewRows([])
    setFailedRows([])
    setFileName(file.name)

    const outcome = await parseCsvFile(file, buildTypeSets(processConfig.data))

    if (outcome.kind === 'error') {
      setError(outcome.message)

      return
    }

    setIsTreeFormat(outcome.isTreeFormat)
    setPreviewRows(outcome.rows)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) parseFile(file)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) parseFile(file)
  }

  const handleRemoveRow = (key: string) => {
    setPreviewRows(prev => prev.filter(r => r.key !== key))
  }

  const handleImport = async () => {
    if (previewRows.length === 0) return
    try {
      const result = await importMutation.mutateAsync({
        rows: previewRows.map(r => ({
          azureDevOpsId: r.azureDevOpsId,
          title: r.title,
          description: r.description,
          epic: r.epic,
          feature: r.feature,
        })),
      })

      const parts: string[] = [`Imported ${result.imported} item${result.imported === 1 ? '' : 's'}`]
      if (result.skipped > 0) parts.push(`skipped ${result.skipped} duplicate${result.skipped === 1 ? '' : 's'}`)
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`)
      const summary = parts.join(', ')

      if (result.failed.length > 0) {
        toast.warning(summary)
        setFailedRows(result.failed)
        setPreviewRows([])
      } else {
        toast.success(summary)
        onOpenChange(false)
      }

      if (result.urlResolutionWarning) {
        toast.warning(result.urlResolutionWarning)
      }
    } catch {
      toast.error('Failed to import work items')
    }
  }

  const previewVisible = previewRows.slice(0, 5)
  const hiddenCount = Math.max(0, previewRows.length - previewVisible.length)
  const canImport = previewRows.length > 0 && !error && !importMutation.isPending
  const orphanCount = previewRows.filter(r => !r.epic && !r.feature).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Import CSV from Azure DevOps</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Upload a CSV export from your DevOps backlog. The CSV must include an <span className="font-medium">ID</span> column.
        </p>

        <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Epic &amp; Feature are read from the CSV hierarchy (<span className="font-medium">Title 1</span>, <span className="font-medium">Title 2</span>, <span className="font-medium">Title 3</span>) when present; otherwise they&apos;re fetched from Azure DevOps.
          </span>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              fileInputRef.current?.click()
            }
          }}
          tabIndex={0}
          className={`flex items-center justify-center h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
            isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 bg-muted/30'
          }`}
          role="button"
          aria-label="CSV file dropzone"
          data-testid="import-azure-devops-dropzone"
        >
          <p className="text-sm text-muted-foreground">
            {fileName ? (
              <span><span className="font-medium text-foreground">{fileName}</span> &mdash; click or drop to replace</span>
            ) : (
              <>Drop your CSV file here / <span className="font-medium text-foreground">Browse Files.</span></>
            )}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="import-azure-devops-file-input"
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">{error}</p>
        )}

        {previewRows.length > 0 && isTreeFormat && orphanCount > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="import-azure-devops-tree-summary">
            {orphanCount} row{orphanCount === 1 ? '' : 's'} without hierarchy will be resolved from Azure DevOps
          </p>
        )}

        {previewVisible.length > 0 && (
          <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="import-azure-devops-preview">
            {previewVisible.map(row => (
              <div
                key={row.key}
                className="flex items-center gap-3 rounded-lg border p-3"
                data-testid={`import-azure-devops-preview-row-${row.key}`}
              >
                <span
                  className="shrink-0 whitespace-nowrap inline-flex items-center rounded-full border bg-external-id-badge border-external-id-badge-border px-2 py-0.5 text-xs font-medium text-external-id-badge-border"
                  data-testid={`import-azure-devops-preview-badge-${row.key}`}
                >
                  ADO #{row.azureDevOpsId}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{row.title}</p>
                  {(row.epic || row.feature) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[row.epic, row.feature].filter(Boolean).join(' / ')}
                    </p>
                  )}
                </div>
                <WorkItemStatusBadge status={WorkItemStatuses.New} />
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  onClick={() => handleRemoveRow(row.key)}
                  aria-label={`Remove row ${row.key}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {hiddenCount > 0 && (
              <p className="text-xs text-muted-foreground">
                + {hiddenCount} more row{hiddenCount === 1 ? '' : 's'} will be imported
              </p>
            )}
          </div>
        )}

        {failedRows.length > 0 && (
          <div
            role="alert"
            className="space-y-2 max-h-[280px] overflow-y-auto rounded-md border border-destructive/40 bg-destructive/5 p-3"
            data-testid="import-azure-devops-failed"
          >
            <p className="text-sm font-medium text-destructive">
              {failedRows.length} row{failedRows.length === 1 ? '' : 's'} failed to import
            </p>
            <ul className="space-y-1.5">
              {failedRows.map((row, idx) => (
                <li
                  key={`failed-${row.azureDevOpsId ?? 'noid'}-${idx}`}
                  className="flex items-start gap-2 text-xs"
                  data-testid={`import-azure-devops-failed-row-${idx}`}
                >
                  <span className="w-16 shrink-0 text-muted-foreground font-medium">
                    {row.azureDevOpsId !== null ? `#${row.azureDevOpsId}` : '—'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium block truncate">{row.title}</span>
                    <span className="text-destructive">{row.errorMessage}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {failedRows.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {failedRows.length === 0 && (
            <Button
              type="button"
              onClick={handleImport}
              disabled={!canImport}
              data-testid="import-azure-devops-confirm"
            >
              <Download className={`h-4 w-4 ${importMutation.isPending ? 'animate-pulse' : ''}`} />
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
